import type { AvailabilityType, AvailableSlot, OperatingWindow } from '../types';
import { validateContinuousTime } from '../utils/availabilityValidation';

type Mode = 'instant' | 'request';

interface Props {
  availabilityType: AvailabilityType;
  slots: AvailableSlot[];
  operatingWindow: OperatingWindow | null;
  /**
   * Para SlotBased: ISO datetime do slot escolhido (vem do server).
   * Para Continuous: ISO datetime construído como `{selectedDate}T{HH:mm}:00`.
   */
  value: string | null;
  /** Recebe ISO datetime tanto pra SlotBased quanto pra Continuous. */
  onChange: (v: string) => void;
  loading: boolean;
  partySize: number;
  /**
   * 'instant' (default): bloqueia horários sem capacidade pra partySize.
   * 'request': horário é apenas intenção do cliente, sem garantia — todos
   * selecionáveis, capacidade exibida só como referência. Operador confirma depois.
   */
  mode?: Mode;
  /**
   * Data selecionada (YYYY-MM-DD) — necessária só pro modo Continuous, pra
   * compor o ISO completo quando o cliente escolhe HH:mm. SlotBased já recebe
   * o ISO pronto do server.
   */
  date: string;
}

function formatTime(iso: string) {
  const d = new Date(iso);
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function isSlotAvailable(slot: AvailableSlot, partySize: number) {
  if (slot.available != null) return slot.available;
  return slot.capacityRemaining >= partySize;
}

// Extrai HH:mm de um ISO datetime sem mudar timezone (o server já retorna
// no fuso da experiência sem 'Z').
function extractTime(iso: string | null): string {
  if (!iso) return '';
  const m = iso.match(/T(\d{2}:\d{2})/);
  return m ? m[1] : '';
}

export function TimeSlotSelector({
  availabilityType,
  slots,
  operatingWindow,
  value,
  onChange,
  loading,
  partySize,
  mode = 'instant',
  date,
}: Props) {
  if (loading) return <div className="slots slots--loading">Carregando horários…</div>;

  // ── Continuous: janela única + input de hora dentro do range ──
  if (availabilityType === 'Continuous') {
    if (!operatingWindow) {
      return (
        <div className="slots slots--empty">
          Sem horários disponíveis para esta data. Tente outra.
        </div>
      );
    }

    const enforceCapacity = mode === 'instant';
    const blocked = enforceCapacity && !operatingWindow.available;

    const handleTimeChange = (hhmm: string) => {
      if (!hhmm || !date) return;
      onChange(`${date}T${hhmm}:00`);
    };

    const currentTime = extractTime(value);
    const rangeCheck = validateContinuousTime(value, operatingWindow);
    // Só sinaliza erro de range quando o usuário JÁ escolheu um horário
    // (currentTime !== ''). Antes disso, o estado é "vazio", não "inválido".
    const showRangeError = !!currentTime && !rangeCheck.valid;

    return (
      <div className="field">
        <span>Horário</span>
        <p className="continuous-window">
          Disponível entre <strong>{operatingWindow.startTime}</strong> e{' '}
          <strong>{operatingWindow.endTime}</strong>. Escolha o horário que preferir.
        </p>
        <input
          type="time"
          className="field__input continuous-time"
          min={operatingWindow.startTime}
          max={operatingWindow.endTime}
          step={900}
          value={currentTime}
          onChange={(e) => handleTimeChange(e.target.value)}
          disabled={blocked}
          aria-invalid={showRangeError || undefined}
        />
        {showRangeError && (
          <small className="error" role="alert">
            {rangeCheck.error}
          </small>
        )}
        {mode === 'request' && !showRangeError && (
          <small className="field__hint">
            Horário solicitado e sujeito à confirmação do operador.
          </small>
        )}
        {blocked && (
          <small className="field__hint field__hint--warn">
            Sem vagas suficientes para {partySize} {partySize === 1 ? 'pessoa' : 'pessoas'} nesta data.
          </small>
        )}
      </div>
    );
  }

  // ── SlotBased: grid de horários discretos (comportamento legado) ──
  if (!slots.length)
    return (
      <div className="slots slots--empty">
        Sem horários disponíveis para esta data. Tente outra.
      </div>
    );

  const enforceCapacity = mode === 'instant';
  const anyAvailable = !enforceCapacity || slots.some((s) => isSlotAvailable(s, partySize));

  return (
    <div className="field">
      <span>Horário</span>
      <div className="slots">
        {slots.map((s) => {
          const fits = isSlotAvailable(s, partySize);
          const disabled = enforceCapacity && !fits;
          const selected = s.startsAt === value;
          const showLow = fits && s.capacityRemaining > 0 && s.capacityRemaining <= 3;
          return (
            <button
              key={s.startsAt}
              type="button"
              className={`slot ${selected ? 'slot--selected' : ''}${disabled ? ' slot--unavailable' : ''}`}
              onClick={() => !disabled && onChange(s.startsAt)}
              disabled={disabled}
              title={
                disabled
                  ? `Sem vagas para ${partySize} ${partySize === 1 ? 'pessoa' : 'pessoas'}`
                  : showLow
                    ? `Apenas ${s.capacityRemaining} ${s.capacityRemaining === 1 ? 'vaga' : 'vagas'}`
                    : undefined
              }
            >
              <span className="slot__time">{formatTime(s.startsAt)}</span>
              {enforceCapacity && showLow && (
                <span className="slot__capacity">
                  {s.capacityRemaining} {s.capacityRemaining === 1 ? 'vaga' : 'vagas'}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {mode === 'request' ? (
        <small className="field__hint">
          Horário solicitado e sujeito à confirmação do operador.
        </small>
      ) : (
        !anyAvailable && (
          <small className="field__hint field__hint--warn">
            Nenhum horário comporta {partySize} {partySize === 1 ? 'pessoa' : 'pessoas'} nesta data. Reduza a quantidade ou escolha outra data.
          </small>
        )
      )}
    </div>
  );
}
